import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Download, Loader2, ChevronRight, GripVertical, Search, Pencil, Check, X, Info, ChevronDown, Plus, AlarmClock } from 'lucide-react';
import { Input } from '../components/ui/input';
import { AddDealDialog } from '../components/AddDealDialog';
import { FunnelExportDialog } from '../components/FunnelExportDialog';
import { HotDealsWidget } from '../components/HotDealsWidget';
import { WeeklyEngagementWidget } from '../components/WeeklyEngagementWidget';
import { OwnerEngagementWidget } from '../components/OwnerEngagementWidget';
import { NdaReminderWidget } from '../components/NdaReminderWidget';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip } from 'recharts';

const OWNER_COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#a855f7'];

const STATIC_STAGES = new Set(['orbis_total', 'olta_tam']);

const OLTA_TAM_DESCRIPTION = `Combined target universe from Orbis: German closely held SMEs (10–200 employees, last reported revenue up to €4.5m, 1–3 shareholders, all 16 federal states). Two ownership profiles merged:

Privately held / succession (31680 from Orbis): Majority owned (51–100%) by 1–3 named individuals, a family, or an owner-manager; shareholders born 1900–1966 (succession age). Excludes companies with reported revenue above €100m.

Captures founder/family businesses approaching succession.`;

const STAGE_COLORS = [
  { bg: '#64748b', light: 'rgba(100,116,139,0.10)', border: 'rgba(100,116,139,0.25)', text: '#94a3b8' },  // Orbis Total
  { bg: '#64748b', light: 'rgba(100,116,139,0.10)', border: 'rgba(100,116,139,0.25)', text: '#94a3b8' },  // Succession Possible
  { bg: '#3b82f6', light: 'rgba(59,130,246,0.10)', border: 'rgba(59,130,246,0.25)', text: '#93c5fd' },    // OLTA TAM (AI Reviewed) — blue
  { bg: '#3b82f6', light: 'rgba(59,130,246,0.10)', border: 'rgba(59,130,246,0.25)', text: '#93c5fd' },    // OLTA Reviewed
  { bg: '#3b82f6', light: 'rgba(59,130,246,0.10)', border: 'rgba(59,130,246,0.25)', text: '#93c5fd' },    // OLTA Approved
  { bg: '#3b82f6', light: 'rgba(59,130,246,0.10)', border: 'rgba(59,130,246,0.25)', text: '#93c5fd' },    // Outreach WIP
  { bg: '#8b5cf6', light: 'rgba(139,92,246,0.10)', border: 'rgba(139,92,246,0.25)', text: '#c4b5fd' },    // Contacted
  { bg: '#8b5cf6', light: 'rgba(139,92,246,0.10)', border: 'rgba(139,92,246,0.25)', text: '#c4b5fd' },    // Response Received
  { bg: '#8b5cf6', light: 'rgba(139,92,246,0.10)', border: 'rgba(139,92,246,0.25)', text: '#c4b5fd' },    // First Call
  { bg: '#8b5cf6', light: 'rgba(139,92,246,0.10)', border: 'rgba(139,92,246,0.25)', text: '#c4b5fd' },    // NDA
  { bg: '#8b5cf6', light: 'rgba(139,92,246,0.10)', border: 'rgba(139,92,246,0.25)', text: '#c4b5fd' },    // On-Site
  { bg: '#8b5cf6', light: 'rgba(139,92,246,0.10)', border: 'rgba(139,92,246,0.25)', text: '#c4b5fd' },    // LOI Signed
  { bg: '#8b5cf6', light: 'rgba(139,92,246,0.10)', border: 'rgba(139,92,246,0.25)', text: '#c4b5fd' },    // Due Diligence
  { bg: '#10b981', light: 'rgba(16,185,129,0.10)', border: 'rgba(16,185,129,0.25)', text: '#6ee7b7' },    // Signing
  { bg: '#10b981', light: 'rgba(16,185,129,0.10)', border: 'rgba(16,185,129,0.25)', text: '#6ee7b7' },    // Closing
  { bg: '#ef4444', light: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.20)', text: '#fca5a5' },      // Passed
];

const STAGE_ICONS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', 'X'];

// Colors keyed by stage slug so every role's funnel uses the SAME scheme per stage,
// regardless of which subset of stages it shows.
const STAGE_ORDER = [
  'orbis_total', 'succession_possible', 'ai_reviewed', 'olta_reviewed', 'olta_approved',
  'outreach_wip', 'contacted', 'response_received',
  'first_call_meeting', 'nda', 'on_site', 'loi_signed',
  'due_diligence', 'signing', 'closing', 'passed',
];
const STAGE_COLOR_BY_KEY = STAGE_ORDER.reduce((acc, key, i) => { acc[key] = STAGE_COLORS[i]; return acc; }, {});
const colorForStage = (stage) => STAGE_COLOR_BY_KEY[stage] || STAGE_COLORS[0];
const iconForStage = (stage) => { const i = STAGE_ORDER.indexOf(stage); return i >= 0 ? STAGE_ICONS[i] : '•'; };

const STAGE_TOOLTIPS = {
  ai_reviewed: 'OLTA TAM: Filtered from the Succession Possible for companies operating within the OLTA sectors: Energy, Utilities, Transportation, Digital, Social/Public.',
  contacted: 'Initial contact made with the business owner.',
  first_call_meeting: 'First call or meeting scheduled/completed — requires a logged call/meeting.',
  nda: 'NDA signed with the company — requires the uploaded NDA document.',
  on_site: 'On-site visit at the company — requires photos + a logged on-site meeting.',
  loi_signed: 'Letter of Intent signed — requires the uploaded signed LOI.',
  due_diligence: 'Due Diligence process in progress.',
  signing: 'Contract signing phase.',
  closing: 'Deal closing.',
};

function DraggableDeal({ project, color, stageKey }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: project.id,
    data: { project, fromStage: stageKey },
  });
  const navigate = useNavigate();
  const style = {
    background: color.light,
    borderColor: isDragging ? color.bg : color.border,
    color: color.text,
    transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      className="inline-flex items-center text-xs px-2 py-0.5 rounded-sm border transition-colors duration-100 cursor-grab active:cursor-grabbing select-none"
      style={style}
      data-testid={`funnel-deal-${project.id}`}
    >
      <span {...attributes} {...listeners} className="mr-1 opacity-50 hover:opacity-100">
        <GripVertical className="w-3 h-3" />
      </span>
      <span onClick={() => navigate(`/projects/${project.id}`)} className="hover:underline cursor-pointer">
        {project.name}
      </span>
      <ChevronRight className="w-3 h-3 inline ml-0.5 opacity-50" />
    </div>
  );
}

function DraggableDealLine({ project, color, stageKey }) {
  const navigate = useNavigate();

  return (
    <div className="flex items-center gap-2 py-1 px-2 rounded-sm hover:bg-secondary/20 transition-colors text-xs group"
      data-testid={`funnel-deal-${project.id}`}>
      <button onClick={() => navigate(`/projects/${project.id}`)}
        className="text-foreground/80 hover:text-blue-400 transition-colors truncate text-left flex-1">
        {project.name}
      </button>
      <ChevronRight className="w-3 h-3 text-muted-foreground/20 shrink-0" />
    </div>
  );
}

function BreakdownBar({ data, colorMap, label }) {
  if (!data || Object.keys(data).length === 0) return null;
  const entries = Object.entries(data).sort((a, b) => b[1].count - a[1].count);
  const total = entries.reduce((s, [, v]) => s + v.count, 0);
  return (
    <div>
      <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider font-bold mb-1.5">{label}</p>
      <div className="flex h-2 rounded-full overflow-hidden bg-secondary/50 mb-1.5">
        {entries.map(([k, v]) => (
          <div key={k} style={{ width: `${(v.count / total) * 100}%`, background: colorMap[k] || '#64748b' }}
            className="transition-all duration-300" />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5">
        {entries.map(([k, v]) => (
          <span key={k} className="text-[10px] text-muted-foreground flex items-center gap-1">
            <span className="w-2 h-2 rounded-full inline-block" style={{ background: colorMap[k] || '#64748b' }} />
            <span className="capitalize">{k.replace(/_/g, ' ')}</span>
            <span className="font-mono text-foreground/70">{v.count.toLocaleString()}</span>
            <span className="text-muted-foreground/40">({v.pct}%)</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function TopList({ items, label }) {
  if (!items || items.length === 0) return null;
  const maxCount = items[0]?.count || 1;
  return (
    <div>
      <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider font-bold mb-1.5">{label}</p>
      <div className="space-y-1">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-[10px] text-foreground/80 truncate">{item.name}</span>
                <span className="text-[10px] font-mono text-muted-foreground ml-2 shrink-0">{item.count.toLocaleString()}</span>
              </div>
              <div className="h-1 rounded-full bg-secondary/50 overflow-hidden">
                <div className="h-full rounded-full bg-blue-500/40 transition-all duration-300"
                  style={{ width: `${(item.count / maxCount) * 100}%` }} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NumericStat({ label, value, suffix }) {
  return (
    <div className="text-center">
      <p className="font-mono text-sm text-foreground/90">{value}{suffix && <span className="text-[10px] text-muted-foreground ml-0.5">{suffix}</span>}</p>
      <p className="text-[9px] text-muted-foreground/50 uppercase tracking-wider">{label}</p>
    </div>
  );
}

function ClaimsPie({ data }) {
  if (!data || !data.owners || data.owners.length === 0) return null;
  const pieData = data.owners.map((o, i) => ({
    name: o.name, value: o.count, pct: o.pct, fill: OWNER_COLORS[i % OWNER_COLORS.length],
  }));
  return (
    <div className="bg-secondary/20 border border-border/50 rounded-sm p-3" data-testid="claims-pie-card">
      <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider font-bold mb-2">Claims by User</p>
      <div className="flex items-center gap-3">
        <div className="shrink-0" style={{ width: 96, height: 96 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={22} outerRadius={44} paddingAngle={2} stroke="none" isAnimationActive={false}>
                {pieData.map((d, i) => <Cell key={i} fill={d.fill} />)}
              </Pie>
              <RTooltip
                formatter={(value, name, p) => [`${value} (${p.payload.pct}%)`, name]}
                contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 4, fontSize: 11 }}
                itemStyle={{ color: 'hsl(var(--foreground))' }}
                labelStyle={{ display: 'none' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="flex-1 min-w-0 space-y-0.5">
          {pieData.map((d, i) => (
            <div key={i} className="flex items-center gap-1.5 text-[10px]">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: d.fill }} />
              <span className="text-foreground/80 truncate flex-1" title={d.name}>{d.name}</span>
              <span className="font-mono text-foreground/70">{d.value.toLocaleString()}</span>
              <span className="text-muted-foreground/40">({d.pct}%)</span>
            </div>
          ))}
        </div>
      </div>
      <p className="text-[9px] text-muted-foreground/40 mt-1.5">
        {data.claimed_total.toLocaleString()} claimed · {data.unclaimed.toLocaleString()} unclaimed
      </p>
    </div>
  );
}

function StageDealsList({ projects }) {
  const navigate = useNavigate();
  const [showDeals, setShowDeals] = useState(false);
  if (!projects || projects.length === 0) return null;
  return (
    <div className="pb-3">
      <button
        onClick={(e) => { e.stopPropagation(); setShowDeals(!showDeals); }}
        className="flex items-center gap-1.5 text-[11px] font-medium text-blue-400 hover:text-blue-300 transition-colors uppercase tracking-wider"
        data-testid="show-stage-deals-btn"
      >
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showDeals ? 'rotate-180' : ''}`} />
        {showDeals ? 'Hide deals' : `Show deals in this stage (${projects.length})`}
      </button>
      {showDeals && (
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-0.5 animate-fade-in-up" data-testid="stage-deals-list">
          {projects.map(p => (
            <div key={p.id} className="flex items-center gap-2 py-1 px-2 rounded-sm hover:bg-secondary/30 transition-colors text-xs group"
              data-testid={`stage-deal-${p.id}`}>
              <button onClick={() => navigate(`/projects/${p.id}`)}
                className="text-foreground/80 hover:text-blue-400 transition-colors truncate text-left flex-1">
                {p.name}
              </button>
              <ChevronRight className="w-3 h-3 text-muted-foreground/30 shrink-0" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StageStatsPanel({ stage }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api.getStageStats(stage)
      .then(res => {
        const data = res.data;
        // Check if backend returned an error in the response body
        if (data.error) {
          setError(`${data.error}\n${data.traceback || ''}`);
        } else {
          setStats(data);
        }
      })
      .catch(err => {
        console.error('Stage stats error:', err);
        const detail = err.response?.data?.detail || err.response?.data?.error || err.message || 'Failed to load statistics';
        setError(detail);
      })
      .finally(() => setLoading(false));
  }, [stage]);

  if (loading) return (
    <div className="flex items-center justify-center py-6">
      <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
      <span className="ml-2 text-xs text-muted-foreground">Computing statistics...</span>
    </div>
  );

  if (error) return (
    <div className="py-3">
      <p className="text-xs text-red-400 font-mono whitespace-pre-wrap break-all">{error}</p>
    </div>
  );

  if (!stats || !stats.stats || stats.total === 0) return (
    <p className="text-xs text-muted-foreground py-3">No data available for this stage.</p>
  );

  const s = stats.stats;

  const successionColors = { yes: '#22c55e', maybe: '#f59e0b', no: '#ef4444' };
  const infraColors = { fit: '#22c55e', fringe: '#f59e0b', no_fit: '#ef4444' };
  const moatColors = { high: '#22c55e', medium: '#f59e0b', low: '#ef4444' };
  const leversColors = { abundant: '#22c55e', available: '#3b82f6', minimal: '#ef4444' };
  const recurringColors = { strong: '#22c55e', medium: '#f59e0b', weak: '#ef4444' };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 py-3 animate-fade-in-up" data-testid={`stage-stats-${stage}`}>
      {/* Claims by User (pie) */}
      {s.claims_by_owner && s.claims_by_owner.claimed_total > 0 && (
        <ClaimsPie data={s.claims_by_owner} />
      )}

      {/* Succession Case */}
      {s.succession_case && Object.keys(s.succession_case).length > 0 && (
        <div className="bg-secondary/20 border border-border/50 rounded-sm p-3">
          <BreakdownBar data={s.succession_case} colorMap={successionColors} label="Succession Case" />
        </div>
      )}

      {/* Infrastructure Adjacent */}
      {s.infrastructure_adjacent && Object.keys(s.infrastructure_adjacent).length > 0 && (
        <div className="bg-secondary/20 border border-border/50 rounded-sm p-3">
          <BreakdownBar data={s.infrastructure_adjacent} colorMap={infraColors} label="Infrastructure Adjacent" />
        </div>
      )}

      {/* Business Model Moat */}
      {s.business_model_moat && Object.keys(s.business_model_moat).length > 0 && (
        <div className="bg-secondary/20 border border-border/50 rounded-sm p-3">
          <BreakdownBar data={s.business_model_moat} colorMap={moatColors} label="Business Model Moat" />
        </div>
      )}

      {/* Levers to 10x */}
      {s.levers_10x && Object.keys(s.levers_10x).length > 0 && (
        <div className="bg-secondary/20 border border-border/50 rounded-sm p-3">
          <BreakdownBar data={s.levers_10x} colorMap={leversColors} label="Levers to 10x" />
        </div>
      )}

      {/* Recurring Revenue */}
      {s.recurring_revenue && Object.keys(s.recurring_revenue).length > 0 && (
        <div className="bg-secondary/20 border border-border/50 rounded-sm p-3">
          <BreakdownBar data={s.recurring_revenue} colorMap={recurringColors} label="Recurring Revenue Potential" />
        </div>
      )}

      {/* Revenue Stats + Brackets */}
      {s.revenue && (
        <div className="bg-secondary/20 border border-border/50 rounded-sm p-3">
          <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider font-bold mb-2">Revenue (€m)</p>
          <div className="flex justify-between mb-2">
            <NumericStat label="Avg" value={s.revenue.avg.toLocaleString()} suffix="€m" />
            <NumericStat label="Min" value={s.revenue.min.toLocaleString()} suffix="€m" />
            <NumericStat label="Max" value={s.revenue.max.toLocaleString()} suffix="€m" />
            <NumericStat label="With Data" value={s.revenue.count.toLocaleString()} />
          </div>
          <div className="flex gap-1 mt-1">
            {Object.entries(s.revenue.brackets).map(([k, v]) => (
              <div key={k} className="flex-1 text-center">
                <div className="h-1.5 rounded-full bg-blue-500/30 overflow-hidden mb-0.5">
                  <div className="h-full bg-blue-400 rounded-full" style={{ width: `${Math.min((v / s.revenue.count) * 100, 100)}%` }} />
                </div>
                <p className="text-[8px] text-muted-foreground/50 leading-tight">{k}</p>
                <p className="text-[9px] font-mono text-foreground/60">{v.toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Employees */}
      {s.employees && (
        <div className="bg-secondary/20 border border-border/50 rounded-sm p-3">
          <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider font-bold mb-2">Employees</p>
          <div className="flex justify-between">
            <NumericStat label="Avg" value={Math.round(s.employees.avg).toLocaleString()} />
            <NumericStat label="Min" value={s.employees.min.toLocaleString()} />
            <NumericStat label="Max" value={s.employees.max.toLocaleString()} />
            <NumericStat label="With Data" value={s.employees.count.toLocaleString()} />
          </div>
        </div>
      )}

      {/* Margin */}
      {s.margins && (
        <div className="bg-secondary/20 border border-border/50 rounded-sm p-3">
          <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider font-bold mb-2">EBITDA Margin</p>
          <div className="flex justify-between">
            <NumericStat label="Avg Margin" value={s.margins.avg} suffix="%" />
            <NumericStat label=">10% Margin" value={s.margins.above_10pct.toLocaleString()} suffix={` (${s.margins.above_10pct_share}%)`} />
            <NumericStat label="With Data" value={s.margins.count.toLocaleString()} />
          </div>
        </div>
      )}

      {/* Shareholder Ages */}
      {s.shareholder_ages && (
        <div className="bg-secondary/20 border border-border/50 rounded-sm p-3">
          <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider font-bold mb-2">Shareholder Ages</p>
          <div className="flex justify-between">
            <NumericStat label="Avg Age" value={s.shareholder_ages.avg} />
            <NumericStat label="60+" value={s.shareholder_ages.age_60_plus.toLocaleString()} />
            <NumericStat label="65+" value={s.shareholder_ages.age_65_plus.toLocaleString()} />
            <NumericStat label="70+" value={s.shareholder_ages.age_70_plus.toLocaleString()} />
          </div>
          <p className="text-[9px] text-muted-foreground/40 mt-1 text-center">{s.shareholder_ages.total_with_age.toLocaleString()} shareholders with age data</p>
        </div>
      )}

      {/* Shareholder Count */}
      {s.shareholder_count && (
        <div className="bg-secondary/20 border border-border/50 rounded-sm p-3">
          <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider font-bold mb-2">Shareholder Count</p>
          <div className="flex justify-between">
            <NumericStat label="Avg" value={s.shareholder_count.avg} />
            <NumericStat label="1 Owner" value={s.shareholder_count.single.toLocaleString()} />
            <NumericStat label="2-3 Owners" value={s.shareholder_count.two_three.toLocaleString()} />
            <NumericStat label="4+" value={s.shareholder_count.four_plus.toLocaleString()} />
          </div>
        </div>
      )}

      {/* Phase 1 Scores */}
      {s.phase1_scores && (
        <div className="bg-secondary/20 border border-border/50 rounded-sm p-3">
          <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider font-bold mb-2">Phase 1 Scores</p>
          <div className="flex justify-between">
            <NumericStat label="Avg Score" value={s.phase1_scores.avg} suffix="%" />
            <NumericStat label=">70%" value={s.phase1_scores.above_70.toLocaleString()} />
            <NumericStat label=">50%" value={s.phase1_scores.above_50.toLocaleString()} />
            <NumericStat label="Screened" value={s.phase1_scores.count.toLocaleString()} />
          </div>
        </div>
      )}

      {/* Phase 2 Scores */}
      {s.phase2_scores && (
        <div className="bg-secondary/20 border border-border/50 rounded-sm p-3">
          <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider font-bold mb-2">Phase 2 Scores</p>
          <div className="flex justify-between">
            <NumericStat label="Avg Score" value={s.phase2_scores.avg} suffix="%" />
            <NumericStat label=">70%" value={s.phase2_scores.above_70.toLocaleString()} />
            <NumericStat label=">50%" value={s.phase2_scores.above_50.toLocaleString()} />
            <NumericStat label="Screened" value={s.phase2_scores.count.toLocaleString()} />
          </div>
        </div>
      )}

      {/* First Screen Status */}
      {(s.first_screen.complete > 0 || s.first_screen.pending > 0) && (
        <div className="bg-secondary/20 border border-border/50 rounded-sm p-3">
          <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider font-bold mb-2">First Screen Status</p>
          <div className="flex h-2 rounded-full overflow-hidden bg-secondary/50 mb-1.5">
            <div style={{ width: `${(s.first_screen.complete / stats.total) * 100}%` }} className="bg-emerald-500/60" />
            <div style={{ width: `${(s.first_screen.pending / stats.total) * 100}%` }} className="bg-amber-500/40" />
          </div>
          <div className="flex justify-between">
            <span className="text-[10px] text-emerald-400 flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500/60" /> Complete {s.first_screen.complete.toLocaleString()}
            </span>
            <span className="text-[10px] text-amber-400 flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-amber-500/40" /> Pending {s.first_screen.pending.toLocaleString()}
            </span>
          </div>
        </div>
      )}

      {/* Top Sectors */}
      {s.top_sectors && s.top_sectors.length > 0 && (
        <div className="bg-secondary/20 border border-border/50 rounded-sm p-3">
          <TopList items={s.top_sectors} label="Top 5 Sectors" />
        </div>
      )}

      {/* Top Regions */}
      {s.top_regions && s.top_regions.length > 0 && (
        <div className="bg-secondary/20 border border-border/50 rounded-sm p-3">
          <TopList items={s.top_regions} label="Top 5 Regions" />
        </div>
      )}
    </div>
  );
}



function SlaBadge({ entries, kind, stage, onOpen }) {
  if (!entries || entries.length === 0) return null;
  const red = kind === 'breached';
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-flex items-center gap-1 text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-sm border cursor-default ${red ? 'bg-red-500/15 border-red-500/40 text-red-400' : 'bg-amber-500/10 border-amber-500/30 text-amber-400'}`}
            onClick={(e) => e.stopPropagation()}
            data-testid={`sla-badge-${kind}-${stage}`}>
            <AlarmClock className="w-3 h-3" /> {entries.length}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-card border-border rounded-sm p-3 max-w-xs">
          <p className={`text-[11px] font-bold uppercase tracking-wider mb-1.5 ${red ? 'text-red-400' : 'text-amber-400'}`}>
            {red ? 'SLA breached' : 'SLA expiring soon'}
          </p>
          <ul className="space-y-1">
            {entries.map((e) => (
              <li key={e.project_id}>
                <button onClick={() => onOpen(e.project_id)}
                  className="text-xs text-foreground hover:text-blue-400 text-left">
                  {e.deal_name || e.deal_label}
                  <span className="text-muted-foreground ml-1.5">
                    {e.owner_name || 'unassigned'}
                    {e.days_left != null && ` · ${e.days_left < 0 ? `${Math.abs(Math.round(e.days_left))}d over` : `${Math.round(e.days_left * 10) / 10}d left`}`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function DroppableStage({ stage, index, funnel, maxCount, isAdmin, editingStage, editValue, onStartEdit, onChangeEdit, onSaveEdit, onCancelEdit, sla, children }) {
  const { isOver, setNodeRef } = useDroppable({ id: stage });
  const navigate = useNavigate();
  const d = funnel.data[stage];
  const hasBreach = (sla?.breached?.length || 0) > 0;
  const isStatic = STATIC_STAGES.has(stage);
  const isEditable = false; // Static stages are no longer editable in the funnel
  const widthPct = Math.max(((d.count / maxCount) * 80) + 20, 25);
  const color = colorForStage(stage);
  const { theme } = useTheme();
  const labelColor = theme === 'light' ? color.bg : color.text;
  const isEditing = editingStage === stage;
  const [expanded, setExpanded] = useState(false);
  const hasDeals = d.count > 0;

  return (
    <div ref={setNodeRef} className="group stagger-item" data-testid={`funnel-stage-${stage}`}>
      <div
        className="relative flex items-center rounded-sm border transition-all duration-300 overflow-hidden"
        style={{
          width: '100%',
          minHeight: '56px',
          background: isOver ? `${color.light}` : color.light,
          borderColor: isOver ? color.bg : (hasBreach ? 'rgba(239,68,68,0.55)' : color.border),
          boxShadow: isOver ? `0 0 12px ${color.bg}40` : (hasBreach ? '0 0 8px rgba(239,68,68,0.15)' : 'none'),
          cursor: hasDeals ? 'pointer' : 'default',
        }}
        onClick={() => hasDeals && setExpanded(!expanded)}
      >
        <span className="font-mono text-lg sm:text-2xl font-bold ml-2 sm:ml-3 mr-2 shrink-0" style={{ color: color.bg, opacity: theme === 'light' ? 0.5 : 0.2 }}>
          {iconForStage(stage)}
        </span>
        <div className="flex-1 flex items-center gap-1 sm:gap-1.5 min-w-0 overflow-hidden flex-wrap py-1">
          <p className="text-xs sm:text-sm font-medium whitespace-nowrap" style={{ color: labelColor }}>{funnel.labels[stage]}</p>
          {funnel.category_breakdown?.[stage]?.length > 0 && (
            <span className="flex items-center gap-1 flex-wrap basis-full sm:basis-auto sm:ml-2" data-testid={`funnel-category-breakdown-${stage}`}
              onClick={(e) => e.stopPropagation()}>
              {funnel.category_breakdown[stage].map(c => (
                <span key={c.id} title={c.id === '__none__' ? 'Deals ohne Import-Kategorie' : `Import-Kategorie${c.archived ? ' (archiviert)' : ''}`}
                  data-testid={`funnel-category-${stage}-${c.id}`}
                  className={`text-[9px] font-mono rounded px-1.5 py-0.5 border whitespace-nowrap ${c.id === '__none__' ? 'text-muted-foreground border-border/60' : 'text-sky-300 border-sky-500/30 bg-sky-500/10'}`}>
                  {c.name} <span className="opacity-80">{c.count.toLocaleString()}</span>
                </span>
              ))}
            </span>
          )}
          {stage === 'succession_possible' && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className="text-foreground/30 hover:text-foreground/70 transition-colors" data-testid="olta-tam-info">
                    <Info className="w-3.5 h-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="bg-card border-border rounded-sm max-w-md p-4">
                  <p className="text-xs text-foreground/90 whitespace-pre-line leading-relaxed">{OLTA_TAM_DESCRIPTION}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {STAGE_TOOLTIPS[stage] && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className="text-foreground/30 hover:text-foreground/70 transition-colors" data-testid={`stage-info-${stage}`}>
                    <Info className="w-3.5 h-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="bg-card border-border rounded-sm max-w-sm p-3">
                  <p className="text-xs text-foreground/90 leading-relaxed">{STAGE_TOOLTIPS[stage]}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {isEditing ? (
          <div className="flex items-center gap-1 mr-3">
            <input type="number" autoFocus value={editValue}
              onChange={e => onChangeEdit(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') onSaveEdit(); if (e.key === 'Escape') onCancelEdit(); }}
              className="w-24 h-8 bg-black/30 border border-white/20 rounded-sm text-right font-mono text-lg text-white px-2 focus:outline-none focus:border-blue-400" />
            <button onClick={onSaveEdit} className="text-emerald-400 hover:text-emerald-300 p-1"><Check className="w-4 h-4" /></button>
            <button onClick={onCancelEdit} className="text-red-400 hover:text-red-300 p-1"><X className="w-4 h-4" /></button>
          </div>
        ) : (
        <div className="flex items-center gap-1.5 mr-4">
          {sla && (sla.breached?.length > 0 || sla.warning?.length > 0) && (
            <span className="flex items-center gap-1 mr-1 shrink-0">
              <SlaBadge entries={sla.breached} kind="breached" stage={stage} onOpen={(pid) => navigate(`/projects/${pid}`)} />
              <SlaBadge entries={sla.warning} kind="warning" stage={stage} onOpen={(pid) => navigate(`/projects/${pid}`)} />
            </span>
          )}
          <span className="font-mono text-lg sm:text-2xl font-bold count-animate" style={{ color: labelColor }}>
            {d.count.toLocaleString()}
          </span>
          {isEditable && isAdmin && (
            <button onClick={(e) => { e.stopPropagation(); onStartEdit(stage, d.count); }}
              className="opacity-0 group-hover:opacity-100 transition-opacity text-foreground/40 hover:text-foreground/80 p-0.5" title="Edit count">
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          {hasDeals && (
            <ChevronDown className={`w-4 h-4 transition-transform text-foreground/40 ${expanded ? 'rotate-180' : ''}`} />
          )}
        </div>
        )}
      </div>
      {expanded && d.count > 0 && (
        <div className="ml-2 sm:ml-4 mt-1 mb-2 border-l-2 border-border pl-3">
          <StageStatsPanel stage={stage} />
          <StageDealsList projects={d.projects} />
        </div>
      )}
    </div>
  );
}

export default function FunnelPage() {
  const navigate = useNavigate();
  const { isAdmin, isAcMember, user } = useAuth();
  const [funnel, setFunnel] = useState(null);
  const [slaHealth, setSlaHealth] = useState(null);
  const [intakeFrozen, setIntakeFrozen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeDeal, setActiveDeal] = useState(null);
  const [funnelSearch, setFunnelSearch] = useState('');
  const [addDealOpen, setAddDealOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [view, setView] = useState('proprietary');
  const [brokered, setBrokered] = useState(null);
  const funnelRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const searchResults = useMemo(() => {
    const q = funnelSearch.trim().toLowerCase();
    if (!funnel || q.length < 2) return [];
    const out = [];
    for (const stage of funnel.stages) {
      for (const p of (funnel.data[stage]?.projects || [])) {
        if ((p.name || '').toLowerCase().includes(q)) {
          out.push({ ...p, stage });
          if (out.length >= 30) return out;
        }
      }
    }
    return out;
  }, [funnel, funnelSearch]);

  const loadFunnel = () => {
    api.getFunnelStats('proprietary')
      .then(res => setFunnel(res.data))
      .catch(() => toast.error('Failed to load funnel data'))
      .finally(() => setLoading(false));
    api.getFunnelStats('brokered').then(res => setBrokered(res.data)).catch(() => {});
    if (!isAcMember) {
      api.getSlaHealth().then(res => setSlaHealth(res.data)).catch(() => {});
      api.getIntakeFreeze().then(res => setIntakeFrozen(!!res.data?.enabled)).catch(() => {});
    }
  };

  useEffect(() => { loadFunnel(); }, []);

  const handleStartEdit = (stage, currentCount) => { setEditingStage(stage); setEditValue(String(currentCount)); };
  const handleCancelEdit = () => { setEditingStage(null); setEditValue(''); };
  const handleSaveEdit = async () => {
    if (!editingStage) return;
    const newVal = parseInt(editValue, 10);
    if (isNaN(newVal) || newVal < 0) { toast.error('Enter a valid number'); return; }
    try {
      const overrides = { ...(funnel?.overrides || {}), [editingStage]: newVal };
      await api.setFunnelOverrides(overrides);
      toast.success(`${funnel.labels[editingStage]} set to ${newVal.toLocaleString()}`);
      setEditingStage(null);
      loadFunnel();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDragStart = (event) => {
    setActiveDeal(event.active.data.current?.project || null);
  };

  const handleDragEnd = async (event) => {
    setActiveDeal(null);
    const { active, over } = event;
    if (!over || !active.data.current) return;

    const fromStage = active.data.current.fromStage;
    const toStage = over.id;
    const projectId = active.id;

    if (fromStage === toStage) return;

    // Only allow moving to the immediate next stage
    const stages = funnel.stages;
    const fromIdx = stages.indexOf(fromStage);
    const toIdx = stages.indexOf(toStage);
    if (toIdx !== fromIdx + 1) {
      toast.error('Deals can only move to the next stage');
      return;
    }

    // Optimistic update
    setFunnel(prev => {
      if (!prev) return prev;
      const updated = JSON.parse(JSON.stringify(prev));
      const project = updated.data[fromStage].projects.find(p => p.id === projectId);
      if (!project) return prev;
      updated.data[fromStage].projects = updated.data[fromStage].projects.filter(p => p.id !== projectId);
      updated.data[fromStage].count--;
      project.pipeline_stage = toStage;
      updated.data[toStage].projects.push(project);
      updated.data[toStage].count++;
      return updated;
    });

    try {
      let reason;
      if (toStage === 'olta_approved') {
        reason = window.prompt('Warum passt der Deal? Kurze Notiz für die Freigabe (optional) — erscheint bei den Approvals als deine Note:');
        if (reason === null) { loadFunnel(); return; }
      }
      await api.updateStage(projectId, toStage, (reason || '').trim() || undefined);
      toast.success(`Moved to ${funnel.labels[toStage]}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && typeof d === 'object' && d.code === 'stage_gate') {
        toast.error(`Missing for ${d.target_label}: ${(d.missing || []).join(' · ')}`, { duration: 8000 });
      } else {
        toast.error(typeof d === 'string' ? d : 'Failed to update stage');
      }
      loadFunnel(); // Revert on failure
    }
  };

  const exportPdf = (data, asOf) => {
    const fn = data || funnel;
    if (!fn) return;
    const isBrokered = fn.view === 'brokered';
    const stages = fn.stages;
    const asOfLabel = asOf ? new Date(asOf + 'T00:00:00').toLocaleDateString('de-DE') : null;
    const printWindow = window.open('', '_blank');
    if (!printWindow) { toast.error('Pop-up blocked'); return; }
    const doc = printWindow.document;
    const styles = `* { box-sizing: border-box; margin: 0; padding: 0; }
body { font-family: 'IBM Plex Sans', sans-serif; background: #fff; color: #18181b; padding: 40px; }
h1 { font-family: 'Barlow Condensed', sans-serif; font-size: 28px; margin-bottom: 4px; }
.subtitle { color: #71717a; font-size: 13px; margin-bottom: 32px; }
.date { color: #a1a1aa; font-size: 11px; margin-bottom: 24px; }
.funnel-row { display: flex; align-items: center; margin-bottom: 12px; }
.stage-bar { height: 48px; border-radius: 4px; display: flex; align-items: center; padding: 0 16px; position: relative; }
.stage-num { font-family: 'JetBrains Mono', monospace; font-size: 24px; font-weight: 700; margin-right: 12px; opacity: 0.3; }
.stage-label { font-size: 13px; font-weight: 500; }
.stage-count { font-family: 'JetBrains Mono', monospace; font-size: 18px; font-weight: 600; margin-left: auto; }
.deal-list { margin-left: 48px; margin-bottom: 8px; }
.deal-item { font-size: 11px; color: #52525b; padding: 2px 0; }
.total-row { margin-top: 20px; padding-top: 16px; border-top: 2px solid #e4e4e7; display: flex; justify-content: space-between; }
.total-label { font-family: 'Barlow Condensed', sans-serif; font-size: 16px; text-transform: uppercase; letter-spacing: 1px; color: #71717a; }
.total-count { font-family: 'JetBrains Mono', monospace; font-size: 24px; font-weight: 700; }
@media print { body { padding: 20px; } }`;
    const body = doc.createElement('body');
    const head = doc.createElement('head');
    const title = doc.createElement('title');
    title.textContent = 'OLTA Deal Funnel';
    head.appendChild(title);
    const link = doc.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;500;600;700&family=IBM+Plex+Sans:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500;600&display=swap';
    head.appendChild(link);
    const style = doc.createElement('style');
    style.textContent = styles;
    head.appendChild(style);
    const h1 = doc.createElement('h1');
    h1.textContent = `OLTA Nachfolge — Deal Funnel (${isBrokered ? 'Brokered' : 'Proprietary'})`;
    body.appendChild(h1);
    const sub = doc.createElement('p');
    sub.className = 'subtitle';
    sub.textContent = isBrokered ? 'Broker-Prozess Stage Overview' : 'Pipeline Stage Overview';
    body.appendChild(sub);
    const dateP = doc.createElement('p');
    dateP.className = 'date';
    const gen = `Generated: ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}`;
    dateP.textContent = asOfLabel ? `Stand per: ${asOfLabel} · ${gen}` : gen;
    body.appendChild(dateP);
    if (asOfLabel && fn.excluded_new) {
      const note = doc.createElement('p');
      note.className = 'date';
      note.textContent = `Stufen rekonstruiert aus der Stage-Historie · ${fn.excluded_new} Deal(s) erst nach dem Stichtag angelegt (nicht enthalten)`;
      body.appendChild(note);
    }
    const amber = { bg: '#b45309', light: 'rgba(217,119,6,0.12)', border: 'rgba(217,119,6,0.35)' };
    const sky = { bg: '#0369a1', light: 'rgba(2,132,199,0.12)', border: 'rgba(2,132,199,0.35)' };
    const slate = { bg: '#475569', light: 'rgba(71,85,105,0.12)', border: 'rgba(71,85,105,0.35)' };
    const metrics = isBrokered ? (fn.metrics || []) : [];
    const maxC = Math.max(
      ...metrics.map(m => m.count),
      ...stages.map(s => fn.data[s].count),
      isBrokered ? (fn.passed_total || 0) : 0, 1);
    const addBar = (labelText, count, color, numText) => {
      const widthPct = Math.max(((count / maxC) * 80) + 20, 25);
      const row = doc.createElement('div');
      row.className = 'funnel-row';
      const bar = doc.createElement('div');
      bar.className = 'stage-bar';
      bar.style.cssText = `width:${widthPct}%;background:${color.light};border:1px solid ${color.border};`;
      const num = doc.createElement('span');
      num.className = 'stage-num';
      num.style.color = color.bg;
      num.textContent = numText;
      bar.appendChild(num);
      const label = doc.createElement('span');
      label.className = 'stage-label';
      label.style.color = color.bg;
      label.textContent = labelText;
      bar.appendChild(label);
      const count_el = doc.createElement('span');
      count_el.className = 'stage-count';
      count_el.style.color = color.bg;
      count_el.textContent = count;
      bar.appendChild(count_el);
      row.appendChild(bar);
      body.appendChild(row);
    };
    // Broker-level metrics (brokered only): contacted → real replies → deals received
    metrics.forEach((m, i) => addBar(m.label, m.count, sky, String(i + 1)));
    stages.forEach((stage, i) => {
      const d = fn.data[stage];
      const color = isBrokered ? amber : colorForStage(stage);
      addBar(fn.labels[stage], d.count, color, isBrokered ? String(metrics.length + i + 1) : iconForStage(stage));
      if (d.projects.length > 0) {
        const list = doc.createElement('div');
        list.className = 'deal-list';
        d.projects.forEach(p => {
          const item = doc.createElement('div');
          item.className = 'deal-item';
          item.textContent = `\u2022 ${p.name}`;
          list.appendChild(item);
        });
        body.appendChild(list);
      }
    });
    // Passed bucket (brokered only)
    if (isBrokered) addBar('Passed', fn.passed_total || 0, slate, String(metrics.length + stages.length + 1));
    const totalRow = doc.createElement('div');
    totalRow.className = 'total-row';
    const totalLabel = doc.createElement('span');
    totalLabel.className = 'total-label';
    totalLabel.textContent = isBrokered ? `Active Brokered Deals (Passed: ${fn.passed_total || 0})` : 'Total Deals in Pipeline';
    totalRow.appendChild(totalLabel);
    const totalCount = doc.createElement('span');
    totalCount.className = 'total-count';
    totalCount.textContent = isBrokered ? fn.total - (fn.passed_total || 0) : fn.total;
    totalRow.appendChild(totalCount);
    body.appendChild(totalRow);
    // about:blank already has <html>; doc.open() would leave documentElement null in Chromium
    doc.documentElement.replaceChildren(head, body);
    setTimeout(() => { printWindow.print(); }, 500);
    toast.success('PDF export opened — use browser print dialog');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (!funnel) return null;

  const maxCount = Math.max(...funnel.stages.map(s => funnel.data[s].count), 1);

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="funnel-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-foreground tracking-tight">Deal Funnel</h1>
          <p className="text-muted-foreground text-xs sm:text-sm mt-1">
            Pipeline stage overview — {funnel.total} deals total
            <span className="ml-2 text-xs text-muted-foreground/60 hidden sm:inline">Click a stage to view statistics</span>
          </p>
          <div className="mt-2 inline-flex border border-border rounded-sm overflow-hidden" data-testid="funnel-view-toggle">
            <button onClick={() => setView('proprietary')} data-testid="funnel-view-proprietary"
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${view === 'proprietary' ? 'bg-emerald-600/20 text-emerald-300' : 'text-muted-foreground hover:text-foreground'}`}>
              Proprietary{funnel.deal_types ? ` (${funnel.deal_types.proprietary || 0})` : ''}
            </button>
            <button onClick={() => setView('brokered')} data-testid="funnel-view-brokered"
              className={`px-3 py-1.5 text-xs font-medium transition-colors border-l border-border ${view === 'brokered' ? 'bg-amber-600/20 text-amber-300' : 'text-muted-foreground hover:text-foreground'}`}>
              Brokered{brokered ? ` (${brokered.total - (brokered.passed_total || 0)})` : ''}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              data-testid="funnel-search-input"
              placeholder="Search deals..."
              value={funnelSearch}
              onChange={e => setFunnelSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') setFunnelSearch(''); }}
              className="pl-8 bg-secondary border-border text-foreground rounded-sm h-8 text-sm w-48"
            />
            {funnelSearch.trim().length >= 2 && (
              <div className="absolute top-full right-0 mt-1 w-80 max-h-80 overflow-y-auto bg-card border border-border rounded-sm shadow-xl z-50"
                data-testid="funnel-search-results">
                {searchResults.length === 0 ? (
                  <p className="text-xs text-muted-foreground px-3 py-2.5">No deals found for &ldquo;{funnelSearch.trim()}&rdquo;</p>
                ) : searchResults.map(p => (
                  <button key={p.id} onClick={() => navigate(`/projects/${p.id}`)}
                    data-testid={`funnel-search-result-${p.id}`}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-secondary/40 transition-colors border-b border-border/40 last:border-b-0">
                    <span className="text-xs text-foreground truncate">{p.name}</span>
                    <span className="text-[9px] font-mono text-muted-foreground border border-border rounded px-1.5 py-0.5 shrink-0">
                      {funnel.labels[p.stage] || p.stage}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {!isAcMember && (
          <Button
            data-testid="funnel-add-deal-btn"
            onClick={() => setAddDealOpen(true)}
            variant="outline"
            className="border-emerald-600/40 text-emerald-400 hover:bg-emerald-600/10 rounded-sm font-medium text-sm h-9 px-4"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Deal
          </Button>
          )}
          {isAdmin && (
          <Button
            data-testid="export-funnel-pdf"
            onClick={() => setExportOpen(true)}
            className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm font-medium text-sm h-9 px-4"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" /> Export
          </Button>
          )}
        </div>
      </div>

      <AddDealDialog open={addDealOpen} onOpenChange={setAddDealOpen} />
      {isAdmin && <FunnelExportDialog open={exportOpen} onOpenChange={setExportOpen} view={view} onPdf={exportPdf} />}

      {intakeFrozen && !isAcMember && (
        <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-sm px-3 py-2 text-xs text-amber-300"
          data-testid="intake-freeze-banner">
          <span className="font-bold">⏸ Deal intake paused</span>
          <span className="text-amber-300/80">— claiming new deals and adding deals to the team meeting is temporarily disabled (admin setting).</span>
        </div>
      )}

      {!isAcMember && <SlaBreachBanner health={slaHealth} />}

      {!isAcMember && <NdaReminderWidget />}

      {!isAcMember && <HotDealsWidget />}

      {!isAcMember && <WeeklyEngagementWidget />}

      {view === 'proprietary' ? (
        <div ref={funnelRef} className="space-y-3" data-testid="funnel-chart">
          {funnel.stages.map((stage, i) => {
            return (
              <DroppableStage key={stage} stage={stage} index={i} funnel={funnel} maxCount={maxCount}
                isAdmin={isAdmin} editingStage={editingStage} editValue={editValue}
                onStartEdit={handleStartEdit} onChangeEdit={setEditValue}
                onSaveEdit={handleSaveEdit} onCancelEdit={handleCancelEdit}
                sla={slaHealth?.stages?.[stage]} />
            );
          })}
        </div>
      ) : (
        <BrokeredFunnelChart funnel={brokered} navigate={navigate} />
      )}

      {!isAcMember && <OwnerEngagementWidget />}

      {view === 'proprietary' && (
      <div className="border-t border-border pt-4 flex items-center justify-between">
        <div>
          <span className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Total Pipeline</span>
          {funnel.deal_types && (
            <span className="ml-3 text-[10px] text-muted-foreground font-mono">
              {funnel.deal_types.proprietary || 0} proprietary · {funnel.deal_types.brokered || 0} brokered
            </span>
          )}
        </div>
        <span className="font-mono text-2xl font-bold text-foreground">{funnel.total}</span>
      </div>
      )}
    </div>
  );
}

// Compact banner: full SLA overview lives on the Pipeline Health page.
function BrokeredFunnelChart({ funnel, navigate }) {
  const [expanded, setExpanded] = useState(null);
  if (!funnel) {
    return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  }
  const metrics = funnel.metrics || [];
  const passedTotal = funnel.passed_total || 0;
  const maxC = Math.max(
    ...metrics.map(m => m.count),
    ...funnel.stages.map(s => funnel.data[s].count),
    passedTotal, 1);
  const barWidth = (n) => Math.max(6, (n / maxC) * 100);
  return (
    <div className="space-y-2.5" data-testid="brokered-funnel-chart">
      <p className="text-xs text-muted-foreground">
        Broker-Prozess — von der Broker-Ansprache bis zum Closing. Ansprache &amp; echte Antworten stammen aus den Kampagnen, die Deal-Stufen aus NDA-Status &amp; Pipeline-Stage. Dieser Funnel geht auch in die AC-Vorab-Mail.
      </p>

      {/* Broker-level metrics: contacted → real replies → deals received */}
      {metrics.map((m) => (
        <div key={m.key} data-testid={`brokered-metric-${m.key}`}>
          <div className="flex items-center gap-3">
            <span className="w-40 sm:w-52 shrink-0 text-xs sm:text-sm text-foreground font-medium">{m.label}</span>
            <div className="flex-1 h-8 bg-secondary/30 rounded-sm overflow-hidden">
              <div className="h-full bg-sky-600/70 rounded-sm flex items-center px-2" style={{ width: `${barWidth(m.count)}%` }}>
                <span className="text-xs font-mono font-bold text-white">{m.count}</span>
              </div>
            </div>
            <span className="w-20 shrink-0 text-right" />
          </div>
        </div>
      ))}

      <div className="border-t border-border/50 !mt-3 !mb-1" />

      {/* Deal-level stages */}
      {funnel.stages.map((stage) => {
        const d = funnel.data[stage];
        const isOpen = expanded === stage;
        return (
          <div key={stage} data-testid={`brokered-stage-${stage}`}>
            <button onClick={() => setExpanded(isOpen ? null : stage)}
              className="w-full text-left group" data-testid={`brokered-stage-btn-${stage}`}>
              <div className="flex items-center gap-3">
                <span className="w-40 sm:w-52 shrink-0 text-xs sm:text-sm text-foreground font-medium">{funnel.labels[stage]}</span>
                <div className="flex-1 h-8 bg-secondary/30 rounded-sm overflow-hidden">
                  <div className="h-full bg-amber-600/70 group-hover:bg-amber-500/80 transition-colors rounded-sm flex items-center px-2"
                    style={{ width: `${barWidth(d.count)}%` }}>
                    <span className="text-xs font-mono font-bold text-white">{d.count}</span>
                  </div>
                </div>
                <span className="w-20 shrink-0 text-[10px] font-mono text-muted-foreground text-right">
                  kum. {funnel.cumulative?.[stage] ?? d.count}
                </span>
              </div>
            </button>
            {isOpen && d.count > 0 && (
              <div className="ml-2 sm:ml-4 mt-1 mb-2 border-l-2 border-amber-600/40 pl-3 space-y-0.5">
                {d.projects.map(p => (
                  <button key={p.id} onClick={() => navigate(`/projects/${p.id}`)}
                    className="block text-xs text-foreground/90 hover:text-amber-300 transition-colors">
                    {p.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Passed bucket */}
      <div data-testid="brokered-stage-passed">
        <div className="flex items-center gap-3">
          <span className="w-40 sm:w-52 shrink-0 text-xs sm:text-sm text-muted-foreground font-medium">Passed</span>
          <div className="flex-1 h-8 bg-secondary/30 rounded-sm overflow-hidden">
            <div className="h-full bg-slate-600/60 rounded-sm flex items-center px-2" style={{ width: `${barWidth(passedTotal)}%` }}>
              <span className="text-xs font-mono font-bold text-white">{passedTotal}</span>
            </div>
          </div>
          <span className="w-20 shrink-0 text-right" />
        </div>
      </div>

      <div className="border-t border-border pt-3 flex items-center justify-between text-xs text-muted-foreground">
        <span>Passed (gesamt): <strong className="text-foreground">{passedTotal}</strong></span>
        <span>Aktiv: <strong className="text-foreground">{funnel.total - passedTotal}</strong></span>
      </div>
    </div>
  );
}

function SlaBreachBanner({ health }) {
  const navigate = useNavigate();
  if (!health || (health.total_breached === 0 && health.total_warning === 0)) return null;
  return (
    <button onClick={() => navigate('/health')}
      className={`w-full flex items-center gap-2 rounded-sm border px-3 py-2 text-xs text-left transition-colors ${health.total_breached > 0 ? 'bg-red-500/5 border-red-500/30 hover:bg-red-500/10' : 'bg-amber-500/5 border-amber-500/25 hover:bg-amber-500/10'}`}
      data-testid="sla-breach-banner">
      <AlarmClock className={`w-4 h-4 shrink-0 ${health.total_breached > 0 ? 'text-red-400' : 'text-amber-400'}`} />
      <span className="text-foreground font-medium">SLA Health:</span>
      {health.total_breached > 0 && <span className="text-red-400 font-bold">{health.total_breached} breached</span>}
      {health.total_breached > 0 && health.total_warning > 0 && <span className="text-muted-foreground">·</span>}
      {health.total_warning > 0 && <span className="text-amber-400 font-bold">{health.total_warning} expiring soon</span>}
      <span className="ml-auto text-muted-foreground shrink-0">View in Pipeline Health →</span>
    </button>
  );
}
